import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators'; 

import { SubjectRelay } from '../../commands/subject-relay.class';

import { MatAutocompleteTrigger, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({ template: ''})
export abstract class FiltrableSelectComponentBase<T> {
	// Fonction simplifiee pour emettre un log
	private log: (message : string, objet?: any) => void =
		(_message : string, _objet?: any) => {};
	//	(message : string, objet?: any) => console.log("FSCB - " + this.id + " - " + message, objet);

	// Le composant de saisie, vu comme un FormControl, pour manipulation de son contenu
	filterInput = new FormControl();

	// Une version securisee de labelGetter
	getLabel: (item: any) => string = (item) => {
		if (item) {
			throw new Error("Label getter missing when called");
		}
		return ''
	};
	
	// Le filtre textuel a appliquer
	protected textFilter$!: Observable<string>;
	
	// La liste actuelle des elements possibles
	protected list$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([] as T[]);
	
	// Un indicateur permettant de relancer le filtrage manuellement;
	protected reset$: Subject<void> = new Subject<void>();

	// La liste actuelle des elements filtres
	filteredItems$!: Observable<T[]>;

	constructor(
	) {
		this.resetCommandsSubject$.subscribe(
			() => {
				this.log("filterInput set to '' by resetCommand");
				this.filterInput.setValue('')
			}
		);

		// Creer le lien entre la liste filtrees et les sorties des deux champs de filtrages
		this.textFilter$ = this.filterInput.valueChanges.pipe(
			startWith(''),
			// Quand on fixe la valeur de l'input de facon programmative, ce pipe est declenche avec l'item en
			// pametre avant que la directive 'displayWith' le transforme en string.
			// Afin d'empecher des erreurs dans la fonction filterList, on realise une preconversion de la valeur du filtre en string.
			map((value: any) => this.toNonNullString(value)),
		);

		// Permet le premier filtrage
		this.reset$.next();
	}

	// Le composant de saisie, vu comme un trigger pour la manipulation de la dropdown list
	@ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) input!: MatAutocompleteTrigger;

	// Identifiant du champ
	@Input() id!: string | undefined;

	// L'indicateur de desactivation du composant
	@Input() disabled: boolean = false;

	// La fonction a utiliser pour obtenir le libelle d'un item
	protected _labelGetter!: (item: T) => string;
	@Input() set labelGetter(func: (item: T) => string) {
		this._labelGetter = func;
		
		// Mise a jout de le version securisee du labelGetter
		this.getLabel = (item: T) => item ? func(item) : '';
	}
	get labelGetter(): (item: T) => string {
		return this._labelGetter;
	}
	
	// Fonction permettant de recuperer la clef unique d'un element de la liste
	// Optionel. Le label normalise sera utilise sinon
	// Sert essentiellement quand le parent fixe la valeur selectedItem programmativement
	@Input() keyGetter: ((a: any) => any) | null = null;

	// La liste source des items possibles en selection, en binding reactif pour pouvoir reagir aux changements venant du parent
	@Input() set list(values: T[]) {
		// Si la reference de la liste change, reproceder au filtrage
		this.list$.next(values);
	}
	get list(): T[] {
		return this.list$.getValue();
	}

	// L'item actuellement selectionne, en biding reactif pour pouvoir reagir aux changements venant du parent
	protected _selectedItem!: T | null;
	@Input() set selectedItem(value: T) {
		this._selectedItem = value;

		// Si on a une fonction pour extraire la clef primaire des elements, on va l'utiliser pour verifier
		// que l'element selectionne est toujours dans la liste des elements
		if (this.keyGetter != null && this.list$.getValue())  {
			let keyGetter = this.keyGetter;
			let selectedKey = keyGetter(this._selectedItem);
			let selectedItemIndex = this.list$.getValue().findIndex($ => keyGetter($) === selectedKey);
			if (selectedItemIndex < 0) {
				// Dans le cas ou ce n'est pas le cas, on efface juste l'item selectionne
				this._selectedItem = null;
				this.selectedItemChange.emit(null);
			}
		}

		this.resetInputValueToSelectedItem();
	}
	get selectedItem(): any {
		return this._selectedItem;
	}

	private resetCommandsSubject$ = new SubjectRelay<void>();
	@Input() set resetCommand(obs: Observable<void> | undefined) {
		this.resetCommandsSubject$.registerSource(obs);
	}

	// L'evenement necessaire au two-way binding pour le selectedItem
	@Output() selectedItemChange = new EventEmitter<T | null>();

	/////////////////////////////////////
	// Gestion des actions utilisateur //
	///////////////////////////////////// 

	onInputFocus() {
		// Lorsque le focus arrive sur le composant, on reinitialise la liste filtree.
		// En effet, le contenu de la liste source peut avoir change meme si sa reference est identique
		this.reset$.next();
	}

	onInputBlur() {
		// Quand on quitte la zone, il se peut que l'utilisateur aie saisi un filtre mais aie quitte sans faire de selection.
		// Dans un tel cas, la zone continue a afficher la saisie, au lieu de revenir a l'item selectionne precedemment.
		// On doit donc remettre en place automatiquement l'item selectionne en place à la sortie de zone.
		//
		// Cependant, selectionner une valeur dans la liste par un clic provoque aussi la sortie de la zone, et le reset de celle-ci risque alors d'annuler la selection.
		// Pour eviter ceci, on delaie le reset automatique, afin que si une selection par clic est en cours, elle puisse s'éxcuter avant.
		setTimeout( //
			() => {
				let inputValue = this.filterInput.value;
				if (inputValue) {
					this.resetInputValueToSelectedItem()
				} else {
					// Cas particulier: on est sorti du champ sans valider, mais apres que l'utiisateur ait vide le champ de saisie
					// Dans ce cas, on remet l'element selectionne a vide
					this._selectedItem = null;
					this.selectedItemChange.emit(null);
				}
			},
			300
		);
	}

	onInputClick() {
		// Contrairement a un <select> la liste ne s'ouvre pas naturellement quand on clique sur le champ apres la selection d'une valeur
		// Il faut donc le faire programativement
		this.ensureDropdownOpen();
	}

	onInputKeyUp(event: any) {
		const key = event.key;
		if (key === 'Enter') {
			// Meme probleme que pour l'evenement click ci-dessus, mais dans le cas de l'utilisation de la touche 'Enter'
			this.ensureDropdownOpen();
		} else if (key === 'Escape') {
			// Si l'utilisateur appuie sur Esc alors que la liste est ouverte, elle se ferme.
			// Cependant, la saisie qu'il a eventuellement fait demeure alors.
			// Il faut restaurer programmativement a ce moment la valeur du dernier item selectionne
			this.resetInputValueToSelectedItem();
		}
	}

	onOptionSelected(event: MatAutocompleteSelectedEvent) {
		// Quand un item est selectionne dans la dropdown list, il faut mettre a jour la donnee du modele
		// Aussi, il est necessaire de lancer les evenements relatifs, pour que le parent se rende compte du changement
		const newSelectedItem = event.option.value as T | null;
		this._selectedItem = newSelectedItem;
		this.selectedItemChange.emit(newSelectedItem);
	}
	
	//////////////////////
	// Fonctions outils //
	//////////////////////

	// Verifie si une valeur est de type string, sinon renvoie ''
	protected toNonNullString(value: any): string {
		if (value === undefined) {
			return '';
		}

		const type = typeof value;
		if (type === "string") {
			return value as string;
		}

		return '';
	}

	// S'assure que la dropdown list est ouverte
	protected ensureDropdownOpen() {
		if (!this.input.panelOpen) {
			this.input.openPanel();
		}
	}


	// Normalise un string pour comparaison sans etre genee par la casse ou certains caracteres speciaux
	protected normalizeValue(value: string | undefined): string {
		if (!value) {
			return '';
		}
		
		let normalizedValue = value
			// Retrait des diacritiques (evol 57229)
			.normalize("NFD")
			.replace(/\p{Diacritic}/gu, "")
			// Mise en minuscules
			.toLowerCase()
			// Remplacement des espaces blancs
			.replace(/\s+/g, ' ')
			.trim()
		;
		
		return normalizedValue;
	}

	// Restore dans l'input la valeur du dernier item selectionne
	protected resetInputValueToSelectedItem() {
		this.log("filterInput set to '" + this.getLabel(this._selectedItem) + "' by resetInputValueToSelectedItem", this._selectedItem);
		this.filterInput.setValue(this._selectedItem);
	}
}